import { getConsentRecord, isAnalyticsAllowed } from './consent';

const VISITOR_STORAGE_KEY = 'fp_visitor_id';
const SESSION_STORAGE_KEY = 'fp_session_id';
const FIRST_TOUCH_STORAGE_KEY = 'fp_utm_first_touch';
const SCROLL_MILESTONES = [25, 50, 75, 100];

type AnalyticsEventType =
  | 'page_view'
  | 'consent_update'
  | 'session_start'
  | 'scroll_depth'
  | 'outbound_click'
  | 'page_exit';

interface UTMData {
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
}

let listenersInitialized = false;
let sessionStartTracked = false;
let currentPageId = '';
let currentPageStartMs = 0;
let currentPageExitSent = false;
let currentMaxScrollPercent = 0;
let reachedScrollMilestones = new Set<number>();
let scrollTicking = false;

function randomId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

function getVisitorId(): string {
  const existing = localStorage.getItem(VISITOR_STORAGE_KEY);
  if (existing) return existing;
  const generated = randomId('v');
  localStorage.setItem(VISITOR_STORAGE_KEY, generated);
  return generated;
}

function getSessionId(): string {
  const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;
  const generated = randomId('s');
  sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
  return generated;
}

function getCurrentUTMData(): UTMData {
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get('utm_source') || '',
    medium: params.get('utm_medium') || '',
    campaign: params.get('utm_campaign') || '',
    term: params.get('utm_term') || '',
    content: params.get('utm_content') || ''
  };
}

function readFirstTouchUTM(): UTMData {
  try {
    const raw = sessionStorage.getItem(FIRST_TOUCH_STORAGE_KEY);
    if (!raw) {
      return { source: '', medium: '', campaign: '', term: '', content: '' };
    }
    return JSON.parse(raw) as UTMData;
  } catch (_) {
    return { source: '', medium: '', campaign: '', term: '', content: '' };
  }
}

function resolveUTMData(): UTMData {
  const current = getCurrentUTMData();
  const hasCurrent = Object.values(current).some(Boolean);
  if (hasCurrent) {
    sessionStorage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(current));
    return current;
  }
  return readFirstTouchUTM();
}

function resolveUTMDataForConsent(consented: boolean): UTMData {
  if (!consented) {
    return getCurrentUTMData();
  }
  return resolveUTMData();
}

function resolveAnalyticsEndpoint(): string {
  const override = process.env.REACT_APP_ANALYTICS_ENDPOINT;
  if (override && override.trim()) return override.trim();

  if (window.location.hostname === 'localhost' && window.location.port === '3000') {
    return 'http://localhost:3001/api/analytics/collect';
  }

  return `${window.location.origin}/admin/api/collect.php`;
}

function inferReferrer(): string {
  return document.referrer || '';
}

function inferBrowserAndOS(userAgent: string): { browser: string; os: string } {
  const ua = userAgent.toLowerCase();

  let browser = 'Desconocido';
  if (ua.includes('edg/')) browser = 'Edge';
  else if (ua.includes('chrome/') && !ua.includes('edg/')) browser = 'Chrome';
  else if (ua.includes('safari/') && !ua.includes('chrome/')) browser = 'Safari';
  else if (ua.includes('firefox/')) browser = 'Firefox';

  let os = 'Desconocido';
  if (ua.includes('windows')) os = 'Windows';
  else if (ua.includes('mac os x')) os = 'macOS';
  else if (ua.includes('android')) os = 'Android';
  else if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ios')) os = 'iOS';
  else if (ua.includes('linux')) os = 'Linux';

  return { browser, os };
}

function getCurrentPath(): string {
  return window.location.pathname + window.location.hash;
}

function getBasePayload(consented = isAnalyticsAllowed()): Record<string, unknown> {
  const userAgent = navigator.userAgent || '';
  const { browser, os } = inferBrowserAndOS(userAgent);

  const payload: Record<string, unknown> = {
    language: navigator.language || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    viewport: {
      width: window.innerWidth || 0,
      height: window.innerHeight || 0
    },
    screen: {
      width: window.screen?.width || 0,
      height: window.screen?.height || 0
    },
    userAgent,
    browser,
    os
  };

  if (consented) {
    payload.visitorId = getVisitorId();
    payload.sessionId = getSessionId();
  }

  return payload;
}

function sendEvent(
  eventType: AnalyticsEventType,
  payload: Record<string, unknown>,
  force = false,
  allowWithoutConsent = false
): void {
  if (!force && !allowWithoutConsent && eventType !== 'consent_update' && !isAnalyticsAllowed()) {
    return;
  }

  const endpoint = resolveAnalyticsEndpoint();
  const body = JSON.stringify({
    eventType,
    payload,
    consent: getConsentRecord()
  });

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
      return;
    }
  } catch (_) {
    // Ignore and fallback to fetch
  }

  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true
  }).catch(() => undefined);
}

function flushPageExit(reason: 'navigation' | 'visibility_hidden' | 'pagehide' | 'beforeunload'): void {
  if (!isAnalyticsAllowed() || !currentPageId || currentPageExitSent || currentPageStartMs <= 0) {
    return;
  }

  currentPageExitSent = true;
  const durationSec = Math.max(0, (Date.now() - currentPageStartMs) / 1000);

  sendEvent('page_exit', {
    ...getBasePayload(),
    pageId: currentPageId,
    path: getCurrentPath(),
    fullUrl: window.location.href,
    reason,
    durationSec,
    maxScrollPercent: currentMaxScrollPercent
  });
}

function trackSessionStartIfNeeded(): void {
  if (!isAnalyticsAllowed() || sessionStartTracked) {
    return;
  }

  sessionStartTracked = true;
  sendEvent('session_start', {
    ...getBasePayload(),
    path: getCurrentPath(),
    fullUrl: window.location.href,
    referrer: inferReferrer(),
    utm: resolveUTMData()
  });
}

function handleScrollEvent(): void {
  scrollTicking = false;

  if (!isAnalyticsAllowed() || !currentPageId) {
    return;
  }

  const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
  const viewport = window.innerHeight || 0;
  const docHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, viewport);
  const denominator = Math.max(docHeight - viewport, 1);
  const scrollPercent = Math.max(0, Math.min(100, Math.round((scrollTop / denominator) * 100)));

  currentMaxScrollPercent = Math.max(currentMaxScrollPercent, scrollPercent);

  SCROLL_MILESTONES.forEach((milestone) => {
    if (scrollPercent >= milestone && !reachedScrollMilestones.has(milestone)) {
      reachedScrollMilestones.add(milestone);
      sendEvent('scroll_depth', {
        ...getBasePayload(),
        pageId: currentPageId,
        path: getCurrentPath(),
        fullUrl: window.location.href,
        scrollPercent: milestone
      });
    }
  });
}

function handleDocumentClick(event: MouseEvent): void {
  if (!isAnalyticsAllowed() || !currentPageId) {
    return;
  }

  const target = event.target as HTMLElement | null;
  const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
  if (!anchor) {
    return;
  }

  const href = anchor.getAttribute('href') || '';
  if (!href || href.startsWith('#')) {
    return;
  }

  let targetUrl: URL;
  try {
    targetUrl = new URL(anchor.href, window.location.href);
  } catch (_) {
    return;
  }

  if (!/^https?:$/i.test(targetUrl.protocol)) {
    return;
  }

  if (targetUrl.host === window.location.host) {
    return;
  }

  sendEvent('outbound_click', {
    ...getBasePayload(),
    pageId: currentPageId,
    path: getCurrentPath(),
    fullUrl: window.location.href,
    linkUrl: targetUrl.toString(),
    linkHost: targetUrl.host
  });
}

export function initializeAnalyticsTracking(): void {
  if (listenersInitialized) {
    return;
  }

  listenersInitialized = true;

  window.addEventListener('scroll', () => {
    if (scrollTicking) {
      return;
    }

    scrollTicking = true;
    window.requestAnimationFrame(handleScrollEvent);
  }, { passive: true });

  document.addEventListener('click', handleDocumentClick, true);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushPageExit('visibility_hidden');
    }
  });

  window.addEventListener('pagehide', () => {
    flushPageExit('pagehide');
  });

  window.addEventListener('beforeunload', () => {
    flushPageExit('beforeunload');
  });
}

export function trackConsentUpdate(status: 'accepted' | 'rejected'): void {
  sendEvent('consent_update', {
    status,
    path: getCurrentPath(),
    fullUrl: window.location.href
  }, true);
}

export function startAnalyticsPageTracking(): void {
  const consented = isAnalyticsAllowed();

  if (consented) {
    trackSessionStartIfNeeded();
    flushPageExit('navigation');
  }

  currentPageId = randomId('p');
  currentPageStartMs = Date.now();
  currentPageExitSent = false;
  currentMaxScrollPercent = 0;
  reachedScrollMilestones = new Set<number>();

  const utm = resolveUTMDataForConsent(consented);
  const isReturningVisitor = consented ? Boolean(localStorage.getItem(VISITOR_STORAGE_KEY)) : false;

  sendEvent('page_view', {
    ...getBasePayload(consented),
    pageId: currentPageId,
    path: getCurrentPath(),
    fullUrl: window.location.href,
    host: window.location.host,
    referrer: inferReferrer(),
    utm,
    utmSource: utm.source,
    utmMedium: utm.medium,
    utmCampaign: utm.campaign,
    utmTerm: utm.term,
    utmContent: utm.content,
    isReturningVisitor,
    cookieless: !consented
  }, false, true);
}
